/**
 * ESCHER FALLS — DOM オーバーレイ HUD。
 *
 * MODULE-CONTRACTS §12 / CORE-SPEC §6。**three.js 禁止。`#ui` の中だけを触る。**
 * 表記はすべて日本語、書体は明朝（`--font-serif`）。
 *
 * DOM 構造は MODULE-CONTRACTS §12.1 のクラス名（`.hud-*` / `.btn` / `.view-dot`）に従い、
 * `src/style.css` のスタイルをそのまま受ける。それに加えて本モジュールは
 * **`ef-` プレフィックスのクラスと専用の `<style>` を自分で document.head に注入する**
 * （結果パネル・パルス・回転アニメーションなど style.css に無い分。style.css は編集しない）。
 */

import { FILL_MS_PER_CELL, ROTATE_DURATION_MS } from '../core/types';
import type { GameState } from '../core/types';
import { formatTime, isTimeCritical, remainingMs } from '../game/timer';
import { stageNumber } from './kanji';
import { t } from './i18n';
import { levelName, levelHint } from './i18nLevels';

export interface HudCallbacks {
  /** 方位を回す。`+1` が右。 */
  onRotate(dir: -1 | 1): void;
  /** 仰角を変える。`+1` が真上寄り。 */
  onElevate(dir: -1 | 1): void;
  /** レバーを倒す。レバーのある面でのみ押せる。 */
  onLever(): void;
  onPour(): void;
  /** 「やりなおす」。面を startView から丸ごと読み直す。 */
  onRetry(): void;
  /**
   * 失敗表示の「もういちど」。**水だけ引いて、視点は今のまま**にする。
   * 省略時は `onRetry` にフォールバックする。
   */
  onAgain?(): void;
  /**
   * 「時を延ばす」。リワード広告を見せて制限時間を延ばす。
   *
   * **省略可**で、省略するとボタン自体を作らない。
   * 広告の提供者が居ないときに押せないボタンを見せないための口である
   * （`game/rewardedAd.ts` の規則1）。
   */
  onExtendTime?(): void;
  /**
   * いま「時を延ばす」を出してよいか。**表示のたびに呼ぶ。**
   *
   * 起動時に一度だけ判定すると、あとから差した提供者・在庫切れ・
   * 買い切りでの広告除去に追随できない。可否は変わるものとして扱う。
   * 省略時は常に出す。
   */
  canExtendTime?(): boolean;
  /**
   * 「時を延ばす」の文言。**表示のたびに呼ぶ。**
   * 資金源が広告か雫かで変わるので、固定文言にできない。
   */
  extendTimeLabel?(): string;
  /**
   * 契約(§12)に無い追加。クリア後の「つぎへ」。
   * **省略可**で、省略時はボタン自体を作らない（main.ts の自動進行に任せる）。
   */
  onNext?(): void;
  /**
   * いま「つぎへ」を出すか。**表示のたびに呼ぶ。** 省略時は常に出す。
   *
   * ふだんは自動で次の面へ進むので「つぎへ」は要らない。
   * **自動進行を止めているときだけ**出す（止めたまま出口が無いと詰む）。
   */
  canNext?(): boolean;
  /**
   * 通水したときの「広告を見て雫を増やす」。**省略可。**
   *
   * `canExtendTime` と同じ規則で、可否も文言も**表示のたびに訊く**。
   * 何面おきに出すかは main.ts が決める（HUD は場所を貸すだけ）。
   */
  onMultiplyPrize?(): void;
  canMultiplyPrize?(): boolean;
  multiplyPrizeLabel?(): string;
  /**
   * 通水した一枚を送り出す。**省略可**で、省略時はボタンを作らない。
   *
   * 押せない場所（ブラウザなど）では `canShare` が false を返すので、
   * **押せないボタンは並ばない。**
   */
  onShare?(): void;
  canShare?(): boolean;
}

export interface Hud {
  /** 状態を反映する。差分だけ DOM を触ること（毎フレーム呼ばれる）。 */
  update(state: GameState): void;
  /**
   * 経過時間を反映する。毎フレーム呼ばれる。
   * `GameState` ではなくここで受けるのは、時間が状態機械の管轄外
   * （`game/timer.ts` が持つ）であり、毎フレーム変わる値で
   * `GameState` の差分比較を無効化したくないため。
   *
   * @param limitMs 制限時間。無ければ null（本編は常に null）。
   */
  setTime(elapsedMs: number, limitMs: number | null): void;
  /** 手持ちの雫を反映する。変わったときだけ呼べばよい。 */
  setDrops(drops: number): void;
  dispose(): void;
}

// =============================================================================
// 注入するスタイル（すべて ef- プレフィックス。style.css のクラスとは衝突しない）
// =============================================================================

const STYLE_ID = 'ef-hud-style';

/** style.css が無い環境でも色が出るよう、var() には必ずフォールバックを書く。 */
const HUD_CSS = `
.ef-veil {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse at 50% 62%,
    rgba(242, 239, 230, 0) 30%,
    rgba(242, 239, 230, 0.72) 100%
  );
  opacity: 0;
  transition: opacity 420ms ease;
}
.ef-veil.is-on { opacity: 1; }

.ef-title { white-space: nowrap; }
/* ヒントは長くなりがち。右上の視点インジケータとステージボタンに食い込まないよう、
   画面幅から右側の占有分を引いた幅で折り返す。 */
.ef-hint {
  max-width: min(52ch, calc(100vw - var(--safe-left, 0px) - var(--safe-right, 0px) - 150px));
  /* 端末幅によっては2行目に1文字だけ残る（実機で「る」だけが落ちた）。
     balance は行長を揃えるので、その泣き別れが消える。 */
  text-wrap: balance;
}
.ef-hint::before { content: '— '; }
.ef-budget { font-variant-numeric: tabular-nums; }
/* 手持ちの雫。**水の色**で出す（彩度を持つのは水だけ、という原則のまま）。
   参照した既存ゲームは右上に金貨を置くが、こちらは右上を視点インジケータが
   使っているので左上の情報群に混ぜる。 */
.ef-drops {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.1em;
  color: var(--water, #4a8c8a);
}
/* 単位は JS で付ける。英語では「600 drops」と後ろに付くので、
   ::before に固定してしまうと訳せない。 */
.ef-drops[hidden] { display: none; }
.ef-time { font-variant-numeric: tabular-nums; letter-spacing: 0.1em; }
/* 制限時間つきの面で、残りが僅かになったら強調する（METAGAME-SPEC §1.3） */
.ef-time.is-critical { color: var(--accent, #c8622d); }
.ef-hint[hidden], .ef-budget[hidden], .ef-time[hidden] { display: none; }

/* 視点インジケータ（VIEW-EXPANSION-SPEC §6）。上から 真上 / 高仰角4 / 低仰角4 の3段。
   どの関係で解けるかは絶対に出さない——出した瞬間に解答表になる。 */
.ef-views { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.ef-view-row { display: flex; gap: 7px; }
.ef-view-label {
  font-size: 10px;
  letter-spacing: 0.24em;
  color: var(--ink-soft, #6b665c);
  margin-top: 1px;
  min-height: 13px;
}
/* 真上のときだけラベルを出す。真上では左右に回しても接続が変わらないので、
   明示しないと「回したのに何も起きない」と誤解される。 */
.ef-view-label:not(.is-on) { visibility: hidden; }

.ef-views.is-turning .view-dot {
  animation: ef-dot-sweep var(--ef-rot-ms, 450ms) ease-in-out;
}
@keyframes ef-dot-sweep {
  0%, 100% { transform: scale(1); }
  45%      { transform: scale(1.6); }
}

/* 放水ボタン。idle の間はつねに活性で、接続の成否では見た目を変えない。
   かつて「つながった瞬間に波紋を出す」演出があったが、解答を教えてしまうので廃した
   （CORE-SPEC §3.1）。非活性になるのは回転中と放水中だけ。 */
.ef-pour:disabled {
  color: var(--ink-soft, #6b665c);
  border-color: var(--ink-soft, #6b665c);
  opacity: 0.34;
}
.ef-pour:not(:disabled) {
  color: var(--water, #4a8c8a);
  border-color: var(--water, #4a8c8a);
}

/* レバー。入っているかどうかが一目で分かること。
   プレイヤーは「どちらの状態か」を覚えながら視点を探すので、ここが読めないと
   「どこから見るか × どの状態か」の二軸の探索が成立しない。 */
/* 方位ボタンの絵（makeSideArrow）。▲ ▼ の文字と同じ色の規則に従わせる。 */
.ef-side-arrow { display: block; margin: auto; fill: currentColor; }
.ef-action-row { display: flex; gap: 10px; align-items: center; }
.ef-lever { letter-spacing: 0.08em; }
.ef-lever.is-on {
  color: var(--paper, #f2efe6);
  background: var(--water, #4a8c8a);
  border-color: var(--water, #4a8c8a);
}
.ef-lever[hidden] { display: none; }

/* クリア / 失敗の表示。中央下スロット（.hud-bottomcenter）の中に積む */
.ef-result {
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: calc(100vw - 32px);
  text-align: center;
  animation: ef-rise 420ms ease-out;
}
.ef-result.is-on { display: flex; }
@keyframes ef-rise {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
}
.ef-result-title { font-size: 30px; letter-spacing: 0.3em; text-indent: 0.3em; white-space: nowrap; }
.ef-result-sub { letter-spacing: 0.22em; }
.ef-actions { display: flex; gap: 12px; margin-top: 4px; }
.ef-actions .btn { white-space: nowrap; }
.ef-pour.is-hidden, .ef-lever.is-hidden { display: none; }

/* 結果表示中は左右のスロットを引っ込める。狭い画面で結果パネルと衝突するうえ、
   どのみち無効化されており「もういちど」と役目が重なるため。 */
.ef-hud-side { transition: opacity 320ms ease; }
.ef-hud-side.is-dimmed { opacity: 0; pointer-events: none; }

/* 狭い画面では、中央下の「放水」と右下の「やりなおす」が横に並びきらず重なる。
   放水のつもりでリトライを押してしまうので、放水だけを一段上へ持ち上げて
   ◀ ▶ ／ やりなおす と段を分ける。
   （上へ逃がすと今度は左上のヒント文とぶつかるため、下段の中で解決する。） */
@media (max-width: 560px) {
  #ui .hud-bottomcenter {
    bottom: calc(var(--hud-gap) + var(--safe-bottom) + 58px);
  }
}
/* 幅だけでは決めきれない場合（レバーがある面、言語で「レバー」の文字が長い場合）は、
   実際に重なっているかを測って持ち上げる（hud.ts の updateStacking）。 */
#ui .hud-bottomcenter.is-stacked {
  bottom: calc(var(--hud-gap) + var(--safe-bottom) + 58px);
}

@media (max-width: 480px) {
  .ef-title { font-size: 17px; }
  .ef-result-title { font-size: 24px; letter-spacing: 0.22em; text-indent: 0.22em; }
  .ef-actions .btn { padding: 9px 14px; letter-spacing: 0.12em; }
}
`;

let styleRefCount = 0;

function injectStyles(): void {
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = HUD_CSS;
  document.head.appendChild(style);
}

function releaseStyles(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount > 0) return;
  document.getElementById(STYLE_ID)?.remove();
}

// =============================================================================
// 小物
// =============================================================================

/** `"03-fall"` のような id の先頭の数字をステージ番号として取り出す。無ければ null。 */
function stageNumberOf(levelId: string): number | null {
  const m = /^\s*(\d+)/.exec(levelId);
  if (!m) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

function stageTitle(levelId: string, name: string): string {
  const n = stageNumberOf(levelId);
  const shown = levelName(levelId, name);
  return n === null ? shown : `${stageNumber(n)} — ${shown}`;
}

/**
 * 現時点で消費した水量（マス数）。
 *
 * `FlowStep` は `cells` を持たないので `duration / FILL_MS_PER_CELL` から復元する
 * （`FlowPlan` の定義上 `duration = cells * FILL_MS_PER_CELL` なので厳密に戻る）。
 * 表示は整数マス単位にしたいので、充填中のセグメントは満ちたマスだけ数える。
 */
function consumedCells(state: GameState): number {
  const plan = state.flow;
  if (plan === null) return 0;
  if (state.phase === 'failed' && plan.budgetExceeded) return plan.totalCells;

  let used = 0;
  for (const step of plan.steps) {
    const cells = Math.max(1, Math.round(step.duration / FILL_MS_PER_CELL));
    const elapsed = state.flowTime - step.startTime;
    if (elapsed <= 0) continue;
    if (elapsed >= step.duration) {
      used += cells;
    } else {
      used += Math.floor((cells * elapsed) / step.duration);
    }
  }
  return used;
}

function makeElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * 方位を回すボタンの絵。**隣の `▲ ▼` と同じ大きさの塗りの三角**を横向きに描く。
 *
 * 以前は `‹ ›` の文字だった。約物なので線が細く小さく、塗りの `▲ ▼` と並ぶと
 * 押せるものに見えにくかった（体験版を PLiCy に載せた際にユーザーから指摘）。
 * 回転台を回る矢印も試したが、拡大すると「元に戻す」の記号に読め、
 * 隣の「やりなおす」と取り違えうるのでやめた。四つを方向キーとして揃える。
 *
 * `◀ ▶` の文字を使わないのは、iOS がこれを絵文字として描くことがあるため
 * （`▲ ▼` には絵文字の形が無い）。寸法は `▲` をヒラギノ明朝 18px で実測した
 * 幅16.2×高さ14.1 を縦横入れ替えたもの。重心が先端の反対に寄って見えるので、
 * 外接矩形の中心から先端側へ 0.6 ずらしてある。色は `currentColor`。
 */
function makeSideArrow(direction: -1 | 1): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'ef-side-arrow');
  const tri = document.createElementNS(SVG_NS, 'polygon');
  tri.setAttribute('points', '4.35,12 18.45,3.9 18.45,20.1');
  if (direction === 1) tri.setAttribute('transform', 'translate(24 0) scale(-1 1)');
  svg.append(tri);
  return svg;
}

function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

function setDisabled(node: HTMLButtonElement, disabled: boolean): void {
  if (node.disabled !== disabled) node.disabled = disabled;
}

/** 見えていない領域をフォーカス・読み上げの対象から外す。 */
function setInert(node: HTMLElement, inert: boolean): void {
  if (node.inert !== inert) node.inert = inert;
}

/** 直前の描画内容。差分だけ DOM に反映するために覚えておく。 */
interface Snapshot {
  title: string;
  hint: string;
  budget: string;
  pose: string;
  phase: string;
  canPour: boolean;
  resultTitle: string;
  resultSub: string;
  timedOut: boolean;
}

// =============================================================================
// createHud
// =============================================================================

/** root は `#ui` 要素。 */
export function createHud(root: HTMLElement, callbacks: HudCallbacks): Hud {
  injectStyles();
  root.replaceChildren();

  // ---- 静的な DOM を一度だけ組む -------------------------------------------

  const veil = makeElement('div', 'ef-veil');

  const topLeft = makeElement('div', 'hud-topleft');
  const titleEl = makeElement('p', 'hud-title ef-title');
  const hintEl = makeElement('p', 'hud-sub ef-hint');
  const budgetEl = makeElement('p', 'hud-sub ef-budget');
  const timeEl = makeElement('p', 'hud-sub ef-time');
  const dropsEl = makeElement('p', 'hud-sub ef-drops');
  hintEl.hidden = true;
  budgetEl.hidden = true;
  topLeft.append(titleEl, hintEl, budgetEl, timeEl, dropsEl);

  const topRight = makeElement('div', 'hud-topright');
  // 視点インジケータ: 仰角3段 × 方位（真上は1つ）。dotEls[elevation][azimuth]。
  const dots = makeElement('div', 'ef-views');
  dots.style.setProperty('--ef-rot-ms', `${ROTATE_DURATION_MS}ms`);
  const dotEls: HTMLElement[][] = [];
  // 画面では上から 真上(2) → 高(1) → 低(0) の順に並べる。
  for (const elevation of [2, 1, 0]) {
    const row = makeElement('div', 'ef-view-row');
    const count = elevation === 2 ? 1 : 4;
    const rowDots: HTMLElement[] = [];
    for (let i = 0; i < count; i += 1) {
      const dot = makeElement('span', 'view-dot');
      dot.setAttribute('aria-hidden', 'true');
      row.append(dot);
      rowDots.push(dot);
    }
    dotEls[elevation] = rowDots;
    dots.append(row);
  }
  const viewLabel = makeElement('p', 'ef-view-label', t.topDown);
  dots.append(viewLabel);
  topRight.append(dots);

  const bottomLeft = makeElement('div', 'hud-bottomleft ef-hud-side');
  const leftBtn = makeElement('button', 'btn btn--round ef-rot-left');
  const rightBtn = makeElement('button', 'btn btn--round ef-rot-right');
  leftBtn.append(makeSideArrow(-1));
  rightBtn.append(makeSideArrow(1));
  leftBtn.type = 'button';
  rightBtn.type = 'button';
  leftBtn.setAttribute('aria-label', t.rotateLeft);
  rightBtn.setAttribute('aria-label', t.rotateRight);
  const upBtn = makeElement('button', 'btn btn--round ef-elev-up', '▲');
  const downBtn = makeElement('button', 'btn btn--round ef-elev-down', '▼');
  upBtn.type = 'button';
  downBtn.type = 'button';
  upBtn.setAttribute('aria-label', t.tiltUp);
  downBtn.setAttribute('aria-label', t.tiltDown);
  bottomLeft.append(leftBtn, rightBtn, upBtn, downBtn);

  const bottomCenter = makeElement('div', 'hud-bottomcenter');
  const result = makeElement('div', 'ef-result');
  result.setAttribute('aria-live', 'polite');
  const resultTitle = makeElement('p', 'hud-title ef-result-title');
  const resultSub = makeElement('p', 'hud-sub ef-result-sub');
  const actions = makeElement('div', 'ef-actions');
  const nextBtn = makeElement('button', 'btn ef-next', t.next);
  nextBtn.type = 'button';
  const againBtn = makeElement('button', 'btn ef-again', t.again);
  againBtn.type = 'button';
  // 広告の入口。提供者が居ないときは append すらしない。
  const extendBtn = makeElement('button', 'btn ef-extend', t.extendByAd);
  extendBtn.type = 'button';
  // 通水したときの上乗せ。**断れる**ので、並びは「つぎへ」より後に置かない
  // ——先に目に入る位置に置くと、押さないと進めないように見える。
  // 文言は `multiplyPrizeLabel()` が開くたびに入れ直す。ここは幅の目安。
  const multiplyBtn = makeElement('button', 'btn ef-multiply', t.multiplyByAd(5));
  multiplyBtn.type = 'button';
  // 共有。**「つぎへ」より後ろに置く。** 先に目に入る位置だと、
  // 送らないと進めないように見える（上乗せと同じ理由）。
  const shareBtn = makeElement('button', 'btn ef-share', t.share);
  shareBtn.type = 'button';
  if (callbacks.onNext) actions.append(nextBtn);
  actions.append(againBtn);
  if (callbacks.onExtendTime) actions.append(extendBtn);
  if (callbacks.onMultiplyPrize) actions.append(multiplyBtn);
  if (callbacks.onShare) actions.append(shareBtn);
  result.append(resultTitle, resultSub, actions);

  const pourBtn = makeElement('button', 'btn btn--water ef-pour', t.pour);
  pourBtn.type = 'button';
  // レバーは放水と並ぶ主要操作なので中央下に置く。
  // 左下（◀ ▶ ▲ ▼）に足すと5つ並んで「やりなおす」と重なる（実機で重なった）。
  const leverBtn = makeElement('button', 'btn ef-lever', t.lever);
  leverBtn.type = 'button';
  leverBtn.setAttribute('aria-label', t.leverAria);
  const actionRow = makeElement('div', 'ef-action-row');
  actionRow.append(leverBtn, pourBtn);
  bottomCenter.append(result, actionRow);

  const bottomRight = makeElement('div', 'hud-bottomright ef-hud-side');
  const retryBtn = makeElement('button', 'btn ef-retry', t.retry);
  retryBtn.type = 'button';
  bottomRight.append(retryBtn);

  root.append(veil, topLeft, topRight, bottomLeft, bottomCenter, bottomRight);

  // ---- イベント -------------------------------------------------------------

  const bound: Array<{ el: HTMLElement; type: string; fn: EventListener }> = [];
  const on = (el: HTMLElement, type: string, fn: EventListener): void => {
    el.addEventListener(type, fn);
    bound.push({ el, type, fn });
  };

  /**
   * 中央下（レバー・放水）が左下の矢印や右下の「やりなおす」と**重なっていたら一段上げる。**
   *
   * 画面幅のしきい値だけで決めると、レバーのある面や「レバー」の訳が長い言語で
   * 漏れる（617px 幅でレバーが ▼ に 8px 重なっていた。2026-10-08 ユーザーの指摘）。
   * 横位置は上げ下げで変わらないので、いまの位置をそのまま測ってよい。
   */
  function updateStacking(): void {
    const c = actionRow.getBoundingClientRect();
    if (c.width === 0) return; // 結果表示中などで隠れている
    const l = bottomLeft.getBoundingClientRect();
    const r = bottomRight.getBoundingClientRect();
    const MARGIN = 12;
    const overlaps = c.left < l.right + MARGIN || c.right > r.left - MARGIN;
    bottomCenter.classList.toggle('is-stacked', overlaps);
  }
  on(window as unknown as HTMLElement, 'resize', () => updateStacking());
  // 初回は配置が決まってから測る（フォントの読み込みで幅が変わる）。
  window.requestAnimationFrame(() => updateStacking());
  void document.fonts?.ready.then(() => updateStacking());

  on(leftBtn, 'click', () => callbacks.onRotate(-1));
  on(rightBtn, 'click', () => callbacks.onRotate(1));
  on(upBtn, 'click', () => callbacks.onElevate(1));
  on(downBtn, 'click', () => callbacks.onElevate(-1));
  on(leverBtn, 'click', () => callbacks.onLever());
  on(pourBtn, 'click', () => callbacks.onPour());
  on(retryBtn, 'click', () => callbacks.onRetry());
  // 「もういちど」は水だけ引く。時間切れのときは表示が「やりなおす」に変わるので、
  // 押されたときの中身も面の読み直しに切り替える（表示と挙動を必ず一致させる）。
  on(againBtn, 'click', () => {
    if (againBtn.textContent === t.retry) callbacks.onRetry();
    else (callbacks.onAgain ?? callbacks.onRetry).call(callbacks);
  });
  if (callbacks.onExtendTime) {
    const onExtendTime = callbacks.onExtendTime.bind(callbacks);
    on(extendBtn, 'click', () => onExtendTime());
  }
  if (callbacks.onNext) {
    const onNext = callbacks.onNext.bind(callbacks);
    on(nextBtn, 'click', () => onNext());
  }
  if (callbacks.onMultiplyPrize) {
    const onMultiplyPrize = callbacks.onMultiplyPrize.bind(callbacks);
    on(multiplyBtn, 'click', () => onMultiplyPrize());
  }
  if (callbacks.onShare) {
    const onShare = callbacks.onShare.bind(callbacks);
    on(shareBtn, 'click', () => onShare());
  }

  // ---- 差分更新 -------------------------------------------------------------

  let prev: Snapshot | null = null;

  function update(state: GameState): void {
    const { level, phase } = state;

    const title = stageTitle(level.id, level.name);
    const hint = levelHint(level.id, level.hint) ?? '';
    const budgetText =
      level.waterBudget === null
        ? ''
        : t.water(Math.max(0, level.waterBudget - consumedCells(state)), level.waterBudget);
    // 手数の上限は二周目だけに付く。残りが分からないと「あと何回回せるか」を
    // 数えながら遊ぶことになり、上限を付けた意味が失われる。
    const movesText =
      level.maxMoves === null
        ? ''
        : t.moves(Math.max(0, level.maxMoves - state.rotations), level.maxMoves);
    const budget = [budgetText, movesText].filter((s) => s !== '').join('　');

    const isClear = phase === 'clear';
    const isFailed = phase === 'failed';
    // **漢字で書く。** 副題（通水・水は行き止まりへ落ちた）が漢字なので、
    // 見出しだけ平仮名だと同じ画面に二つの調子が混じる。
    const resultTitleText = isClear ? t.resultClear : isFailed ? t.resultFail : '';
    // 失敗の理由を出し分ける。「なぜ駄目だったか」が分からないと直しようがない。
    // flow が null のまま failed になるのは、放水せずに手数を使い切った場合だけ。
    // 時間切れは flow から逆算できない（放水と無関係に起きる）ので最初に見る。
    const resultSubText = isClear
      ? t.clearTitle
      : isFailed
        ? t.fail[
            state.timedOut
              ? 'time'
              : state.flow === null
                ? 'moves'
                : state.flow.drained
                  ? 'drywell'
                  : state.flow.budgetExceeded
                    ? 'water'
                    : 'deadend'
          ]
        : '';

    const next: Snapshot = {
      title,
      hint,
      budget,
      pose: `${state.pose.elevation}:${state.pose.azimuth}`,
      phase,
      canPour: state.canPour,
      resultTitle: resultTitleText,
      resultSub: resultSubText,
      timedOut: state.timedOut,
    };

    if (prev === null || prev.title !== title) setText(titleEl, title);

    if (prev === null || prev.hint !== hint) {
      setText(hintEl, hint);
      hintEl.hidden = hint === '';
    }

    if (prev === null || prev.budget !== budget) {
      setText(budgetEl, budget);
      budgetEl.hidden = budget === '';
    }

    if (prev === null || prev.pose !== next.pose) {
      const { azimuth, elevation } = state.pose;
      for (let el = 0; el < dotEls.length; el += 1) {
        const row = dotEls[el];
        for (let az = 0; az < row.length; az += 1) {
          // 真上の段は点が1つだけ。方位は絵の向きしか変えないので区別して見せない。
          const on = el === elevation && (el === 2 || az === azimuth);
          row[az].classList.toggle('is-active', on);
        }
      }
      viewLabel.classList.toggle('is-on', elevation === 2);
    }

    if (prev === null || prev.phase !== phase) {
      const showResult = isClear || isFailed;
      dots.classList.toggle('is-turning', phase === 'rotating');
      result.classList.toggle('is-on', showResult);
      veil.classList.toggle('is-on', showResult);
      pourBtn.classList.toggle('is-hidden', showResult);
      leverBtn.classList.toggle('is-hidden', showResult);
      resultTitle.classList.toggle('is-clear', isClear);
      bottomLeft.classList.toggle('is-dimmed', showResult);
      bottomRight.classList.toggle('is-dimmed', showResult);
    }

    if (prev === null || prev.resultTitle !== resultTitleText) setText(resultTitle, resultTitleText);
    if (prev === null || prev.resultSub !== resultSubText) setText(resultSub, resultSubText);

    // 上乗せの出し分け。**毎回訊く**——広告の在庫も、何面おきかの数え方も、
    // 買い切りでの除去も、あとから変わる。ここを一度で決めてはいけない。
    // `nextBtn` と違って結果表示のたびに評価するので、prev の比較には載せない。
    if (callbacks.onMultiplyPrize) {
      const canMultiply = isClear && callbacks.canMultiplyPrize?.() !== false;
      multiplyBtn.hidden = !canMultiply;
      if (canMultiply) {
        const label = callbacks.multiplyPrizeLabel?.();
        if (label) setText(multiplyBtn, label);
      }
    }
    if (callbacks.onNext) {
      nextBtn.hidden = !(isClear && callbacks.canNext?.() !== false);
    }
    if (callbacks.onShare) {
      shareBtn.hidden = !(isClear && callbacks.canShare?.() !== false);
    }

    if (prev === null || prev.timedOut !== state.timedOut) {
      // 時間切れのときだけ「もういちど」を「やりなおす」に差し替える。
      // 水を引いても時間は戻らないので、そこに「もういちど」を出すのは嘘になる。
      // 押し場所を変えずに意味だけ変えるのは、指が同じ場所を探すから。
      setText(againBtn, state.timedOut ? t.retry : t.again);
      // 延長は時間切れのときにしか意味がない。加えて、いま広告を出せるかは
      // そのつど訊く（提供者の差し込み・在庫切れ・買い切りで変わるため）。
      extendBtn.hidden = !state.timedOut || callbacks.canExtendTime?.() === false;
      if (!extendBtn.hidden) {
        const label = callbacks.extendTimeLabel?.();
        if (label) setText(extendBtn, label);
      }
    }

    // 放水ボタンは idle の間つねに押せる（CORE-SPEC §3.1）。
    // **接続が成立しているかどうかで見た目を変えてはならない。**
    // ここで解が分かってしまうと、盤面を見ずに総当たりで解けてしまう。
    const pourEnabled = phase === 'idle' && state.canPour;
    setDisabled(pourBtn, !pourEnabled);

    // 見えていないボタンをタブ順から外す。結果表示中は放水ボタンが display:none、
    // 通常時は結果パネルごと消えているので、キーボードで掴めてはいけない。
    const showingResult = isClear || isFailed;
    setInert(pourBtn, showingResult);
    setInert(result, !showingResult);
    setInert(bottomLeft, showingResult);
    setInert(bottomRight, showingResult);

    const rotateEnabled = phase === 'idle';
    setDisabled(leftBtn, !rotateEnabled);
    setDisabled(rightBtn, !rotateEnabled);
    // 仰角は端で行き止まるので、押せない向きは非活性にして「効かない」ことを見せる。
    setDisabled(upBtn, !rotateEnabled || state.pose.elevation >= 2);
    setDisabled(downBtn, !rotateEnabled || state.pose.elevation <= 0);
    // レバーの無い面ではボタンごと隠す。押せないボタンを並べても混乱するだけ。
    if (leverBtn.hidden !== !state.hasLever) {
      leverBtn.hidden = !state.hasLever;
      updateStacking();
    }
    setDisabled(leverBtn, !rotateEnabled);
    if (leverBtn.classList.contains('is-on') !== state.leverOn) {
      leverBtn.classList.toggle('is-on', state.leverOn);
    }

    prev = next;
  }

  /**
   * 制限時間があれば残り、無ければ経過を出す。
   * 表示は 1/100 秒まで動くので、**文字列が変わったときだけ DOM を触る**
   * （`setText` が差分を見ている）。
   */
  function setTime(elapsedMs: number, limitMs: number | null): void {
    const left = remainingMs(elapsedMs, limitMs);
    setText(timeEl, left === null ? formatTime(elapsedMs) : t.timeLeft(formatTime(left)));
    const critical = isTimeCritical(elapsedMs, limitMs);
    if (timeEl.classList.contains('is-critical') !== critical) {
      timeEl.classList.toggle('is-critical', critical);
    }
  }

  function dispose(): void {
    for (const { el, type, fn } of bound) el.removeEventListener(type, fn);
    bound.length = 0;
    root.replaceChildren();
    releaseStyles();
  }

  /** 手持ちの雫を反映する。差分だけ触る（毎フレーム呼ばれても平気なこと）。 */
  function setDrops(drops: number): void {
    setText(dropsEl, t.drops(Math.max(0, Math.floor(drops))));
  }

  return { update, setTime, setDrops, dispose };
}
